import { useState, useEffect, useCallback, useRef } from 'react';
import { getErrorMessage } from '@/lib/utils';

export interface UseApiResult<T> {
  /** The fetched data, or `null` before the first successful load. */
  data: T | null;
  /** `true` while a request is in flight. */
  loading: boolean;
  /** Human-readable error message, or `null` when there is no error. */
  error: string | null;
  /** Re-run the fetcher. Useful for retry buttons and post-mutation refreshes. */
  refetch: () => void;
}

/**
 * Generic data-fetching hook that replaces the repeated
 * `useState(loading) + useState(error) + useState(data) + useEffect + try/catch/finally`
 * boilerplate found across pages.
 *
 * @param fetcher  Async function that returns the data.
 * @param deps     Dependency array — the fetcher re-runs when any value changes.
 *                 Pass `[]` for fetch-on-mount only.
 *
 * @example
 * const { data: teams, loading, error, refetch } = useApi(() => teamApi.getAll(), []);
 *
 * @example
 * // Re-fetch when a filter changes
 * const { data, loading, error } = useApi(
 *   () => logApi.getLogs(page, pageSize),
 *   [page, pageSize],
 * );
 */
export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Keep a stable reference to the latest fetcher so we can call it from
  // refetch() without adding `fetcher` to the effect's dependency array.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      setData(result);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refetch: load };
}
